import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';
import Navbar from '../../components/Navbar';
import { useAuth } from '../../context/AuthContext';

const MAX_IMAGES_FREE = 10;    // mirrors IMAGE_MAX_UPLOAD_COUNT in settings.py
const MAX_FILE_MB = 5;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png']);

export default function NewProject() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [deadline, setDeadline] = useState('');
  const [labelClasses, setLabelClasses] = useState([]);
  const [labelInput, setLabelInput] = useState('');
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fileInputRef = useRef();

  // ── Label class tag helpers ──────────────────────────────────────────────
  function addLabel() {
    const tag = labelInput.trim();
    if (tag && !labelClasses.includes(tag)) {
      setLabelClasses(prev => [...prev, tag]);
    }
    setLabelInput('');
  }

  function handleLabelKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addLabel();
    }
  }

  function removeLabel(tag) {
    setLabelClasses(prev => prev.filter(t => t !== tag));
  }

  // ── Image file helpers ───────────────────────────────────────────────────
  function validateAndAddFiles(incoming) {
    const combined = [...files, ...incoming];
    if (combined.length > MAX_IMAGES_FREE) {
      setError(`Maximum ${MAX_IMAGES_FREE} images allowed.`);
      return;
    }
    for (const f of incoming) {
      if (!ALLOWED_TYPES.has(f.type)) {
        setError(`"${f.name}": only JPG and PNG are allowed.`);
        return;
      }
      if (f.size > MAX_FILE_BYTES) {
        setError(`"${f.name}": exceeds ${MAX_FILE_MB} MB.`);
        return;
      }
    }
    setError('');
    setFiles(combined);
  }

  function handleFileInput(e) {
    validateAndAddFiles(Array.from(e.target.files));
    e.target.value = '';
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    validateAndAddFiles(Array.from(e.dataTransfer.files));
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setError('');
  }

  // ── Submit ───────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Project name is required.');
      return;
    }
    if (labelClasses.length === 0) {
      setError('Add at least one label class.');
      return;
    }
    if (files.length === 0) {
      setError('Upload at least one image.');
      return;
    }

    const form = new FormData();
    form.append('name', name.trim());
    form.append('label_classes', JSON.stringify(labelClasses));
    form.append('instructions', instructions);
    if (deadline) form.append('deadline', deadline);
    files.forEach(f => form.append('images', f));

    setSubmitting(true);
    setProgress(0);

    try {
      await client.post('/api/projects/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      updateUser({ free_trial_used: true });
      navigate('/dashboard', { state: { showBanner: true } });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Submission failed. Please try again.';
      setError(msg);
      setProgress(0);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.content}>
        <h1 style={styles.title}>New Project</h1>

        <form onSubmit={handleSubmit} style={styles.form}>

          {/* Project name */}
          <label style={styles.label}>Project Name *</label>
          <input
            style={styles.input}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Street Signs Q3"
            disabled={submitting}
          />

          {/* Label classes */}
          <label style={styles.label}>Label Classes *</label>
          <div style={styles.tagRow}>
            {labelClasses.map(tag => (
              <span key={tag} style={styles.tag}>
                {tag}
                <button
                  type="button"
                  onClick={() => removeLabel(tag)}
                  style={styles.tagRemove}
                  disabled={submitting}
                >×</button>
              </span>
            ))}
          </div>
          <div style={styles.tagInputRow}>
            <input
              style={{ ...styles.input, flex: 1 }}
              value={labelInput}
              onChange={e => setLabelInput(e.target.value)}
              onKeyDown={handleLabelKeyDown}
              placeholder="Type a class and press Enter"
              disabled={submitting}
            />
            <button
              type="button"
              onClick={addLabel}
              style={styles.addBtn}
              disabled={submitting}
            >Add</button>
          </div>

          {/* Instructions */}
          <label style={styles.label}>Instructions</label>
          <textarea
            style={styles.textarea}
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            rows={4}
            placeholder="Annotation guidelines for workers…"
            disabled={submitting}
          />

          {/* Deadline */}
          <label style={styles.label}>Deadline (optional)</label>
          <input
            type="date"
            style={styles.input}
            value={deadline}
            onChange={e => setDeadline(e.target.value)}
            disabled={submitting}
          />

          {/* Image upload */}
          <label style={styles.label}>
            Images (JPG / PNG, max {MAX_FILE_MB} MB each, up to {MAX_IMAGES_FREE}) *
          </label>
          <div
            style={{
              ...styles.dropZone,
              ...(dragging ? styles.dropZoneActive : {}),
            }}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInput}
              disabled={submitting}
            />
            {files.length === 0 ? (
              <p style={styles.dropHint}>
                Drag &amp; drop images here, or <u>click to browse</u>
              </p>
            ) : (
              <p style={styles.dropHint}>
                {files.length} / {MAX_IMAGES_FREE} image{files.length !== 1 ? 's' : ''} selected
                {files.length < MAX_IMAGES_FREE ? ' — click to add more' : ''}
              </p>
            )}
          </div>

          {files.length > 0 && (
            <ul style={styles.fileList}>
              {files.map((f, i) => (
                <li key={i} style={styles.fileItem}>
                  <span>{f.name} <span style={styles.fileSize}>({(f.size / 1024 / 1024).toFixed(2)} MB)</span></span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    style={styles.removeFileBtn}
                    disabled={submitting}
                  >Remove</button>
                </li>
              ))}
            </ul>
          )}

          {/* Progress bar */}
          {submitting && (
            <div style={styles.progressWrap}>
              <div style={{ ...styles.progressBar, width: `${progress}%` }} />
              <span style={styles.progressLabel}>{progress}%</span>
            </div>
          )}

          {error && <p style={styles.error}>{error}</p>}

          <div style={styles.actions}>
            <button
              type="button"
              style={styles.cancelBtn}
              onClick={() => navigate('/dashboard')}
              disabled={submitting}
            >Cancel</button>
            <button
              type="submit"
              style={styles.submitBtn}
              disabled={submitting}
            >{submitting ? 'Uploading…' : 'Create Project'}</button>
          </div>

        </form>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f0f2f5', fontFamily: 'sans-serif' },
  content: { padding: '2rem', maxWidth: '640px', margin: '0 auto' },
  title: { fontSize: '1.6rem', color: '#2c3e50', marginBottom: '1.5rem' },
  form: { background: '#fff', borderRadius: '8px', padding: '1.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' },
  label: { display: 'block', fontWeight: 600, marginBottom: '0.35rem', marginTop: '1rem', color: '#333', fontSize: '0.9rem' },
  input: {
    width: '100%', boxSizing: 'border-box',
    padding: '0.55rem 0.75rem', border: '1px solid #ccc',
    borderRadius: '6px', fontSize: '0.9rem', outline: 'none',
  },
  textarea: {
    width: '100%', boxSizing: 'border-box',
    padding: '0.55rem 0.75rem', border: '1px solid #ccc',
    borderRadius: '6px', fontSize: '0.9rem', resize: 'vertical', outline: 'none',
  },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem' },
  tag: {
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
    background: '#2c3e50', color: '#fff',
    padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.82rem',
  },
  tagRemove: {
    background: 'none', border: 'none', color: '#fff',
    cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: 0,
  },
  tagInputRow: { display: 'flex', gap: '0.5rem' },
  addBtn: {
    padding: '0.55rem 1rem', background: '#2c3e50', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem',
    whiteSpace: 'nowrap',
  },
  dropZone: {
    marginTop: '0.5rem', border: '2px dashed #aaa', borderRadius: '8px',
    padding: '2rem 1rem', textAlign: 'center', cursor: 'pointer',
    background: '#fafafa', transition: 'border-color 0.15s, background 0.15s',
  },
  dropZoneActive: { borderColor: '#2c3e50', background: '#eef0f3' },
  dropHint: { margin: 0, color: '#555', fontSize: '0.9rem' },
  fileList: { listStyle: 'none', padding: 0, margin: '0.75rem 0 0' },
  fileItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.4rem 0', borderBottom: '1px solid #f0f0f0', fontSize: '0.87rem', color: '#333',
  },
  fileSize: { color: '#888', fontSize: '0.8rem' },
  removeFileBtn: {
    background: 'none', border: 'none', color: '#c0392b',
    cursor: 'pointer', fontSize: '0.82rem',
  },
  progressWrap: {
    position: 'relative', height: '20px', background: '#eee',
    borderRadius: '10px', overflow: 'hidden', margin: '1rem 0 0.25rem',
  },
  progressBar: {
    height: '100%', background: '#2c3e50',
    transition: 'width 0.2s ease', borderRadius: '10px',
  },
  progressLabel: {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)', fontSize: '0.75rem',
    fontWeight: 700, color: '#fff',
  },
  error: { color: '#c0392b', margin: '0.75rem 0 0', fontSize: '0.88rem' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' },
  cancelBtn: {
    padding: '0.6rem 1.2rem', background: '#eee', color: '#333',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem',
  },
  submitBtn: {
    padding: '0.6rem 1.4rem', background: '#2c3e50', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem',
  },
};
